import { useContext, useEffect, useState } from 'react';
import { A01 } from 'api/U005';
import { AppContext } from 'templates/AppContextProvider';

export interface Audition {
  audition_info: {
    audition_id: number;
    title: string;
    lp_url: string;
    end_dtm: Date;
    decline_kbn: string;
  };

  examination_info: {
    examination_no: number;
    mypage_txt: string;
    examination_kbn: string;
    confirmed: string;
    examination_title: string;
    examination_venue: string;
    examination_sort_no: number;
    examination_s_no: number;
    start_dtm: Date;
    end_dtm: Date;
    meeting_time: string;
    result: string;
  };
  group: {
    group_ap: string;
    member_num: number;
    group_approval: string;
    applied: string;
  }[];

  group_member?: {
    consenter: string;
    display_name: string;
    notice_flg: string;
    admin_approval: string;
  }[];
  reason: {
    sno: string;
    svalue: string;
    svalue2: string;
    svalue3: string;
    svalue4: string;
  }[];
  next_path: string;
  specific_use: string;
  privacy_notice: string;
  audition_terms: string;
  now_dtm: Date;
}

export function useGetAuditions(
  userId: string,
  auditionId: string
): [Audition | undefined, boolean, Error | null] {
  const ctx = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [audition, setAudition] = useState<Audition>();
  const [err, setError] = useState<Error | null>(null);

  const fetchAudition = async (
    userId: string,
    auditionId: string,
    cancellation: AbortSignal
  ): Promise<Audition> => {
    const json = await A01(userId, auditionId, ctx.currentLang, cancellation);

    return {
      audition_info: json.audition_info,
      examination_info: json.examination_info,
      group: json.group,
      group_member: json.group_member,
      next_path: json.next_path,
      reason: json.reason,
      specific_use: json.specific_use,
      privacy_notice: json.privacy_notice,
      audition_terms: json.audition_terms,
      now_dtm: new Date(json.now_dtm),
    };
  };

  useEffect(() => {
    if (!userId || !auditionId) {
      return;
    }
    const controller = new AbortController();
    const cancellation = controller.signal;
    async function fetch() {
      try {
        setLoading(true);
        setError(null);
        const audition = await fetchAudition(userId, auditionId, cancellation);
        setAudition(audition);
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err : new Error(`unknown error. ${err}`));
      } finally {
        setLoading(false);
      }
    }

    fetch();
    return () => {
      controller.abort();
    };
  }, [auditionId, userId, ctx.currentLang]);

  return [audition, loading, err];
}
